import { useState } from "react";
import { toast } from "sonner";
import {
  Loader2,
  MessageCircle,
  Lock,
  Check,
  ArrowRight,
} from "lucide-react";
import { Reveal } from "../lib/motion";
import { useLang } from "../lib/i18n";
import { CtaButton, Kicker, Watermark } from "./Shared";

const BG_IMG = "/images/contact.webp";
const EMPTY = {
  name: "",
  company: "",
  email: "",
  requestType: "",
  message: "",
  website: "",
};
const REQUIRED = ["name", "email", "company", "requestType", "message"];
const PROMISE_ICONS = [MessageCircle, Lock, Check];

const Field = ({ id, label, error, children }) => (
  <div>
    <label htmlFor={id} className="mb-1.5 block text-xs text-warm font-light">
      {label} <span aria-hidden="true">*</span>
    </label>
    {children}
    {error ? (
      <p id={`${id}-error`} className="contact-field-error" role="alert">
        {error}
      </p>
    ) : null}
  </div>
);

/**
 * Contact / devis — fond contact.png, formulaire à droite.
 */
export const Contact = () => {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
    if (errors[k]) setErrors((err) => ({ ...err, [k]: "" }));
  };

  const validate = () => {
    const next = {};
    REQUIRED.forEach((k) => {
      if (!String(form[k] || "").trim()) next[k] = c.toast.fieldRequired;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error(c.toast.required);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/contact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success) {
        if (data.mocked) {
          toast.error(c.toast.mocked || c.toast.error);
        } else {
          toast.success(c.toast.success);
          setForm(EMPTY);
          setErrors({});
        }
      } else {
        toast.error(c.toast.error);
      }
    } catch (err) {
      toast.error(c.toast.fail);
    } finally {
      setLoading(false);
    }
  };

  const fieldProps = (id, key) => ({
    id,
    "data-testid": id.startsWith("input") || id.startsWith("select") ? id : undefined,
    className: `contact-input${errors[key] ? " is-invalid" : ""}`,
    value: form[key],
    onChange: update(key),
    required: true,
    "aria-invalid": errors[key] ? true : undefined,
    "aria-describedby": errors[key] ? `${id}-error` : undefined,
  });

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative overflow-hidden bg-canvas"
    >
      <div
        className="pointer-events-none absolute inset-y-0 left-0 z-0 w-full max-w-xl lg:max-w-[46%] xl:max-w-[42%]"
        aria-hidden
      >
        <img
          src={BG_IMG}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-contain object-left opacity-32 -translate-y-6 md:-translate-y-10"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-canvas/25 via-canvas/45 to-canvas max-lg:bg-gradient-to-t max-lg:from-canvas/90 max-lg:via-canvas/40 max-lg:to-canvas/55" />
        <div className="absolute inset-0 bg-gradient-to-tr from-canvas/75 via-canvas/25 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-canvas/80 to-transparent" />
      </div>

      <Watermark n="06" />

      <div className="relative z-10 max-w-site mx-auto px-6 md:px-12 pt-16 sm:pt-20 md:pt-28 pb-20 sm:pb-24 md:pb-36 grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 lg:gap-16 items-start">
        <Reveal className="min-w-0">
          <Kicker className="mb-5 sm:mb-6">{c.kicker}</Kicker>

          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl uppercase leading-tight">
            <span className="block text-cream">{c.title[0]}</span>
            <span className="block text-gradient">{c.title[1]}</span>
          </h2>

          <p className="mt-5 sm:mt-6 text-sm sm:text-base text-warm font-light leading-relaxed max-w-md">
            {c.desc}
          </p>

          <div className="relative mt-10 sm:mt-12 md:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-0">
            {c.promises.map((item, i) => {
              const Icon = PROMISE_ICONS[i];
              return (
                <div
                  key={item.title}
                  className={`flex flex-col items-center sm:items-start text-center sm:text-left px-1 sm:px-2 md:px-3 ${
                    i > 0 ? "sm:border-l sm:border-copper-light/35" : ""
                  }`}
                >
                  <span className="inline-flex items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-full border border-copper/70 text-copper">
                    <Icon size={18} strokeWidth={1.55} aria-hidden />
                  </span>
                  <p className="mt-4 text-sm text-cream font-normal leading-snug">
                    {item.title}
                  </p>
                  <p className="mt-1.5 text-xs text-dim font-light leading-snug max-w-[12rem] sm:max-w-[10.5rem] md:max-w-[11rem]">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={0.1} className="w-full min-w-0">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="space-y-4 sm:space-y-5 rounded-2xl border border-copper-light/45 bg-canvas/85 sm:bg-canvas/80 backdrop-blur-sm p-5 sm:p-6 md:p-8"
            noValidate
          >
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={update("website")}
              className="hidden"
              aria-hidden="true"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field id="input-name" label={c.labels.name} error={errors.name}>
                <input
                  {...fieldProps("input-name", "name")}
                  data-testid="input-name"
                  placeholder={c.placeholders.name}
                  autoComplete="name"
                />
              </Field>
              <Field id="input-email" label={c.labels.email} error={errors.email}>
                <input
                  {...fieldProps("input-email", "email")}
                  data-testid="input-email"
                  type="email"
                  placeholder={c.placeholders.email}
                  autoComplete="email"
                />
              </Field>
            </div>

            <Field id="input-company" label={c.labels.company} error={errors.company}>
              <input
                {...fieldProps("input-company", "company")}
                data-testid="input-company"
                placeholder={c.placeholders.company}
                autoComplete="organization"
              />
            </Field>

            <Field id="select-request-type" label={c.labels.type} error={errors.requestType}>
              <select
                {...fieldProps("select-request-type", "requestType")}
                data-testid="select-request-type"
                className={`contact-input cursor-pointer${errors.requestType ? " is-invalid" : ""}`}
                style={{ colorScheme: "dark" }}
              >
                <option value="" disabled>
                  {c.typePlaceholder}
                </option>
                {c.requestTypes.map((rt) => (
                  <option key={rt.value} value={rt.value} style={{ background: "var(--bg-primary)" }}>
                    {rt.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field id="input-message" label={c.labels.message} error={errors.message}>
              <textarea
                {...fieldProps("input-message", "message")}
                data-testid="input-message"
                rows={4}
                className={`contact-input resize-none${errors.message ? " is-invalid" : ""}`}
                placeholder={c.placeholders.message}
              />
            </Field>

            <p className="text-xs text-dim font-light">{c.rgpd.required}</p>

            <details className="group rounded-xl border border-copper-light/25 bg-section/35 px-4 py-3 text-xs text-warm font-light leading-relaxed">
              <summary className="flex cursor-pointer list-none items-center gap-2 select-none text-cream [&::-webkit-details-marker]:hidden [&::marker]:hidden">
                <svg
                  viewBox="0 0 10 12"
                  width="12"
                  height="14"
                  className="shrink-0 transition-transform duration-200 group-open:rotate-90"
                  aria-hidden
                >
                  <polygon points="0,0 10,6 0,12" fill="#7cb8ff" />
                </svg>
                <span>{c.rgpd.summary}</span>
              </summary>
              <div className="mt-3 space-y-2">
                {c.rgpd.paragraphs.map((text) => (
                  <p key={text}>{text}</p>
                ))}
              </div>
            </details>

            <CtaButton
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="group w-full hover:opacity-90 transition-opacity duration-300 disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" aria-hidden /> {c.sending}
                </>
              ) : (
                <>
                  {c.submit}
                  <ArrowRight
                    size={15}
                    aria-hidden
                    className="group-hover:translate-x-0.5 transition-transform duration-300"
                  />
                </>
              )}
            </CtaButton>

            <p className="flex items-center justify-center gap-2 text-[11px] text-dim font-light">
              <Lock size={12} className="text-copper" aria-hidden />
              {c.secure}
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
